import { For, Show, createEffect, createMemo, createSignal, untrack, type JSX } from 'solid-js';
import { createStore } from 'solid-js/store';
import Text from './Text';
import ItemStackView from './ItemStackView';
import { showDialog } from './Dialog';
import { useToast } from './MessageBox';
import { useApi, isOk } from '../api';
import { useTranslation } from '../hooks/useTranslation';
import { useDebounced } from '../hooks/useDebounced';
import { showSkullOwnerPicker } from './SkullOwnerPicker';
import { resolveSkin } from '../services/skullTexture';
import { downloadSkin } from './SkullOwnerView';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { getEnchantmentName, ENCHANTMENT_IDS } from '../services/enchantmentNames';
import { maxDurability } from '../services/itemDurability';
import { parseSnbt } from '../services/snbtReader';
import { stripColorCodes } from '../services/itemDisplayName';
import {
    opsFor, comparesDamageField, blankRule, ruleTypeIcon,
    dimensionKey as dimensionKeyOf, dimensionLabelOf, sameRule, asRequest,
} from '../services/itemMatchRules';
import type { ItemMatchRuleDto, ItemMatchRuleRequest, ItemStackDto, SellbotDimensionDto } from '../api/generated/schemas';
import type { ItemDef } from '../services/craftingResolver';
import type { ItemStack } from '../models/itemStack';
import styles from '../pages/Sellbot.module.css';
import { firstError } from '../api/envelope';

// The identity matrix a sellbot offer and an AH watch both edit: same rows, same states, same styles.
// The owner supplies the sample, its stored rules and the server-computed dimensions, plus the calls
// that persist one dimension and render the preview, which is all that differs between the two.
export interface ItemIdentityRuleApi {
    create: (rule: ItemMatchRuleRequest) => Promise<{ ok: boolean; error?: string }>;
    update: (ruleId: string, rule: ItemMatchRuleRequest) => Promise<{ ok: boolean; error?: string }>;
    remove: (ruleId: string) => Promise<{ ok: boolean; error?: string }>;
    reload: () => Promise<void>;
    preview: (request: { itemId: number; metadata: number; rules: ItemMatchRuleRequest[] }) => Promise<ItemStackDto | null>;
}

export interface ItemIdentityEditorProps {
    itemId: number;
    metadata: number;
    snbt?: string | null;
    rules: ItemMatchRuleDto[];
    dimensions: SellbotDimensionDto[];
    ruleApi: ItemIdentityRuleApi;
    headerAction?: JSX.Element;
}

const DIMENSION_STATES = ['Original', 'Ignored', 'Custom'] as const;

// A list category holds entries the operator adds to; every other category holds one value.
const LIST_KINDS = ['Lore', 'Enchantment', 'NbtContains'] as const;

// Vanilla 1.8 skull item id, mirroring SellbotOfferDimensions.SkullItemId: a SkullOwner row makes
// sense only on the one item family that ever carries the tag.
const SKULL_ITEM_ID = 397;

// An entry cannot be Original: the category answers absence, and removing the entry is how you say it.
const ENTRY_STATES = ['Custom', 'Ignored'] as const;

// An entry the rules do not cover is reachable, by retargeting a sibling onto another key, and a select
// with no matching option renders blank with no editor and no way out.
function entryStates(state: string): readonly string[] {
    return ENTRY_STATES.includes(state as typeof ENTRY_STATES[number]) ? ENTRY_STATES : DIMENSION_STATES;
}

// Mirrors SellbotMatchPlan.CategoryLoreLine: this line addresses the lore category, not one of its lines.
const CATEGORY_LORE_LINE = -1;

function isListKind(kind: string): boolean {
    return (LIST_KINDS as readonly string[]).includes(kind);
}

function addressesCategory(dimension: SellbotDimensionDto): boolean {
    if (!isListKind(dimension.kind)) return true;
    return dimension.kind === 'Lore' ? dimension.index === CATEGORY_LORE_LINE : !dimension.path;
}

function defaultCustomOp(kind: string): string {
    if (kind === 'Lore') return 'Contains';
    if (kind === 'NbtContains') return 'Exists';
    if (kind === 'Enchantment') return 'Ge';
    return 'Eq';
}

// Seeded from whatever the row already asserts, so switching a rule's operator keeps its value. A row
// with no rule is vanilla and has nothing to seed from, bar the variant the offer is for.
function ruleFromDimension(dimension: SellbotDimensionDto, operator: string,
    baseMetadata: number): ItemMatchRuleRequest {
    const base = blankRule(dimension.kind, operator);
    const number = Number(dimension.value ?? 0) || 0;
    if (comparesDamageField(dimension.kind)) return { ...base, numericValue: dimension.value == null ? baseMetadata : number };
    if (dimension.kind === 'Lore') return { ...base, numericValue: dimension.index, stringValue: dimension.value ?? '' };
    if (dimension.kind === 'Enchantment') return { ...base, stringValue: dimension.path, numericValue: number };
    if (dimension.kind === 'NbtContains') return { ...base, stringValue: dimension.path };
    if (dimension.kind === 'DisplayName') return { ...base, stringValue: dimension.value ?? '' };
    if (dimension.kind === 'RepairCost') return { ...base, numericValue: number };
    return base;
}

// Mirrors SellbotOfferDimensions.KeyOf on the server, which the rows are matched against by key.
function dimensionKey(dimension: SellbotDimensionDto): string {
    return dimensionKeyOf(dimension.kind, dimension.index, dimension.path ?? null);
}

// Per-dimension vanilla baseline, D1 included: docs/sellbot/item-identity.md §1.
function originalValue(dimension: SellbotDimensionDto, baseMetadata: number): string | null {
    if (dimension.kind === 'DurabilityRemaining' || dimension.kind === 'RepairCost') return '0';
    if (dimension.kind === 'Metadata') return String(baseMetadata);
    return null;
}

const SIGNATURE_MAX_TEXT_LINES = 2;

// The rule stores the signature's lines joined by newline, the shape SellbotSignature parses out.
function signatureTextLines(text: string): string[] {
    const lines = (text ?? '').split('\n').slice(0, SIGNATURE_MAX_TEXT_LINES);
    while (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
    return lines;
}

function withSignatureTextLine(text: string, lineIndex: number, value: string): string {
    const lines = signatureTextLines(text);
    while (lines.length <= lineIndex) lines.push('');
    lines[lineIndex] = value;
    return lines.join('\n').replace(/\n+$/, '');
}

export default function ItemIdentityEditor(props: ItemIdentityEditorProps) {
    const { t } = useTranslation();
    const toast = useToast();
    const api = useApi();
    const resolver = useCraftingResolver();

    // Keyed by dimension, not by rule: a null draft is a pending "no rule", which no rule id could carry.
    const [ruleDrafts, setRuleDrafts] = createStore<Record<string, { rule: ItemMatchRuleRequest | null } | undefined>>({});

    const itemVariants = createMemo<ItemDef[]>(() => resolver()?.getVariantsById(props.itemId) ?? []);
    const variantLabel = (variant: ItemDef): string =>
        `${resolver()?.getLocalizedName(variant.key) ?? variant.displayName} (${variant.metadata})`;
    const itemDurabilityMax = (): number | undefined => maxDurability(resolver()?.getItemById(props.itemId, 0)?.name);
    const itemTagCount = () => parseSnbt(props.snbt ?? undefined).tagCount ?? 0;

    const ruleOfDimension = (dimension: SellbotDimensionDto) =>
        dimension.ruleId ? props.rules.find(rule => rule.id === dimension.ruleId) : undefined;

    // Draft-shaped, so "no rule" compares as a real null against a draft rather than as a missing entry.
    const storedRule = (dimension: SellbotDimensionDto): ItemMatchRuleRequest | null => {
        const rule = ruleOfDimension(dimension);
        return rule ? asRequest(rule) : null;
    };

    // A staged "no rule" is a null inside the draft, so collapsing the two with ?? would read it as no change.
    const draftOf = (dimension: SellbotDimensionDto): ItemMatchRuleRequest | null => {
        const draft = ruleDrafts[dimensionKey(dimension)];
        return draft ? draft.rule : storedRule(dimension);
    };

    const isDimensionDirty = (dimension: SellbotDimensionDto) => {
        const draft = ruleDrafts[dimensionKey(dimension)];
        if (!draft) return false;
        const stored = storedRule(dimension);
        if (draft.rule === null || stored === null) return draft.rule !== stored;
        return !sameRule(draft.rule, stored);
    };

    // One draft covers all three states, so Save applies whichever of create, update or delete it implies.
    const stateOf = (dimension: SellbotDimensionDto): string => {
        const draft = draftOf(dimension);
        if (draft === null) return 'Original';
        return draft.operator === 'Ignore' ? 'Ignored' : 'Custom';
    };

    const setDimensionState = (dimension: SellbotDimensionDto, state: string) => {
        if (state === stateOf(dimension)) return;
        if (state === 'Original') {
            // An added row has no stored rule, so vanilla is what it was before it existed. Staging a
            // null draft against a stored null reads as clean, which would strand it with no Cancel.
            if (!dimension.ruleId) revertDimension(dimension);
            else setRuleDrafts(dimensionKey(dimension), { rule: null });
            return;
        }

        // Keep what the operator already typed when they toggle a row back and forth.
        const keptRule = draftOf(dimension)
            ?? ruleFromDimension(dimension, defaultCustomOp(dimension.kind), props.metadata);
        const operator = state === 'Ignored' ? 'Ignore'
            : keptRule.operator === 'Ignore' ? defaultCustomOp(dimension.kind)
                : keptRule.operator;
        setRuleDrafts(dimensionKey(dimension), { rule: { ...keptRule, operator } });
    };

    const revertDimension = (dimension: SellbotDimensionDto) => {
        setRuleDrafts(dimensionKey(dimension), undefined);
        // An added row has no stored state to revert to, so cancelling it means it never existed.
        if (!dimension.ruleId) setAddedDimensions(rows => rows.filter(row => dimensionKey(row) !== dimensionKey(dimension)));
    };

    const saveDimension = async (dimension: SellbotDimensionDto) => {
                const draft = draftOf(dimension);

        const res = draft === null
            ? await props.ruleApi.remove(dimension.ruleId!)
            : dimension.ruleId
                ? await props.ruleApi.update(dimension.ruleId, draft)
                : await props.ruleApi.create(draft);

        if (!res.ok) {
            toast?.error(res.error ?? t(draft === null ? 'SELLBOT_TOAST_DELETE_FAILED' : 'SELLBOT_TOAST_UPDATE_FAILED'));
            return;
        }
        revertDimension(dimension);
        await props.ruleApi.reload();
    };

    // An added entry has no server dimension to hang off yet, so it lives here until its draft is saved.
    const [addedDimensions, setAddedDimensions] = createStore<SellbotDimensionDto[]>([]);

    const dimensions = createMemo(() => [...(props.dimensions), ...addedDimensions]);
    const dimensionsOfKind = (kind: string) => dimensions().filter(dimension => dimension.kind === kind);

    // Damage reads as wear on a damageable item and as the variant on everything else, so its kind
    // comes from the item rather than being fixed here.
    const categories = createMemo<string[]>(() => {
        const damageKind = dimensionsOfKind('DurabilityRemaining').length > 0 ? 'DurabilityRemaining' : 'Metadata';
        return [damageKind, 'DisplayName', 'Lore', 'Signature', 'Enchantment', 'RepairCost', 'NbtContains', 'NbtTagCount',
            ...(props.itemId === SKULL_ITEM_ID ? ['SkullOwner'] : [])];
    });

    const categoryRow = (kind: string) => dimensionsOfKind(kind).find(addressesCategory);
    const entryRows = (kind: string) =>
        dimensionsOfKind(kind).filter(dimension => !addressesCategory(dimension));

    // Custom with nothing in it is stored identically to Original, so an empty Custom category has to be
    // remembered here or its entry list could never be opened.
    const [openedCategories, setOpenedCategories] = createSignal<string[]>([]);

    const categoryMode = (kind: string): string => {
        if (!isListKind(kind)) {
            const categoryRule = categoryRow(kind);
            return categoryRule ? stateOf(categoryRule) : 'Original';
        }
        const categoryRule = categoryRow(kind);
        if (categoryRule && stateOf(categoryRule) === 'Ignored') return 'Ignored';
        if (entryRows(kind).some(entry => stateOf(entry) !== 'Original')) return 'Custom';
        return openedCategories().includes(kind) ? 'Custom' : 'Original';
    };

    const [categoryBusy, setCategoryBusy] = createSignal<string | null>(null);

    // A category mode spans several rules, so it applies on change; entry values still stage behind Save
    // and Cancel below.
    const setCategoryMode = async (kind: string, mode: string) => {
        if (mode === categoryMode(kind)) return;

        setOpenedCategories(open => mode === 'Custom'
            ? [...open.filter(openKind => openKind !== kind), kind]
            : open.filter(openKind => openKind !== kind));

        setCategoryBusy(kind);
        try {
            const categoryRule = categoryRow(kind);

            // Before dropping the entries, not after: the create is server-validated and can be rejected
            // by a collision, and losing the entries first would leave the offer demanding an absent
            // category, which matches nothing at all.
            if (mode === 'Ignored' && !categoryRule?.ruleId) {
                const created = await props.ruleApi.create({
                    ...blankRule(kind, 'Ignore'),
                    numericValue: kind === 'Lore' ? CATEGORY_LORE_LINE : 0,
                });
                if (!created.ok) {
                    toast?.error(created.error ?? t('SELLBOT_TOAST_UPDATE_FAILED'));
                    await props.ruleApi.reload();
                    return;
                }
            }

            if (mode === 'Original' || mode === 'Ignored')
                // Every entry row, not only the saved ones: an unsaved add would otherwise keep feeding
                // the preview from a card the collapsed category no longer renders.
                for (const entry of entryRows(kind)) {
                    revertDimension(entry);
                    if (!entry.ruleId) continue;
                    const removed = await props.ruleApi.remove(entry.ruleId);
                    if (!removed.ok) { toast?.error(t('SELLBOT_TOAST_DELETE_FAILED')); await props.ruleApi.reload(); return; }
                }

            if (mode !== 'Ignored' && categoryRule?.ruleId) {
                revertDimension(categoryRule);
                const removed = await props.ruleApi.remove(categoryRule.ruleId);
                if (!removed.ok) { toast?.error(t('SELLBOT_TOAST_DELETE_FAILED')); await props.ruleApi.reload(); return; }
            }

            await props.ruleApi.reload();
        } finally { setCategoryBusy(null); }
    };

    // Two rows sharing a key would share one draft and let the server store two rules for one
    // dimension, of which the matrix can only ever surface and delete the first.
    const addDimension = (kind: string, index: number, path: string | null, rule: ItemMatchRuleRequest) => {
        const dimension: SellbotDimensionDto = { kind, index, path, value: null, state: 'Original', ruleId: null };
        if (dimensions().some(existing => dimensionKey(existing) === dimensionKey(dimension))) {
            toast?.info(t('SELLBOT_DIM_ADD_EXISTS', dimensionLabel(dimension)));
            return;
        }
        setAddedDimensions(addedDimensions.length, dimension);
        setRuleDrafts(dimensionKey(dimension), { rule });
    };

    // Removing the last rule of a repeatable dimension asserts "none of these", and also takes away the
    // row that would have said so, so the preview states it instead.
    const forbiddenKinds = createMemo(() => ([
        ['Lore', 'SELLBOT_DIM_NONE_LORE'],
        ['Enchantment', 'SELLBOT_DIM_NONE_ENCHANTMENT'],
        ['NbtContains', 'SELLBOT_DIM_NONE_NBT'],
    ] as const)
        .filter(([kind]) => !dimensionsOfKind(kind).some(dimension => stateOf(dimension) !== 'Original'))
        .map(([, key]) => key));

    const addLoreLine = () => {
        const nextLine = dimensionsOfKind('Lore').reduce((highest, dimension) => Math.max(highest, dimension.index + 1), 0);
        addDimension('Lore', nextLine, null, { ...blankRule('Lore', 'Contains'), numericValue: nextLine, stringValue: '' });
    };

    // Retargeting a row onto an id another row already holds would collide their drafts, so the picker
    // offers only free ids plus whichever one this row is on.
    const selectableEnchantmentIds = (dimension: SellbotDimensionDto) => {
        const rowedElsewhere = new Set(dimensionsOfKind('Enchantment')
            .filter(other => dimensionKey(other) !== dimensionKey(dimension))
            .map(other => other.path));
        return ENCHANTMENT_IDS.filter(id => !rowedElsewhere.has(String(id)));
    };

    const addEnchantment = () => {
        const rowedEnchantmentIds = new Set(dimensionsOfKind('Enchantment').map(dimension => dimension.path));
        const freeEnchantmentId = ENCHANTMENT_IDS.find(id => !rowedEnchantmentIds.has(String(id)));
        if (freeEnchantmentId === undefined) { toast?.info(t('SELLBOT_DIM_ADD_ENCHANTMENT_FULL')); return; }
        addDimension('Enchantment', 0, String(freeEnchantmentId),
            { ...blankRule('Enchantment', 'Ge'), stringValue: String(freeEnchantmentId), numericValue: 1 });
    };

    // An empty path matches nothing, so the key is asked for up front instead of dropping the offer to stock zero.
    const addNbtTag = async () => {
        const answer = await showDialog({
            title: t('SELLBOT_DIM_ADD_NBT'),
            description: t('SELLBOT_DIM_ADD_NBT_HINT'),
            fields: [{ key: 'path', label: t('SELLBOT_RULE_PATH_VALUE'), placeholder: 'HideFlags' }],
        });
        const path = answer?.path?.trim();
        if (!path) return;
        addDimension('NbtContains', 0, path, { ...blankRule('NbtContains', 'Exists'), stringValue: path });
    };

    const editRule = (dimension: SellbotDimensionDto, patch: Partial<ItemMatchRuleRequest>) => {
        const current = draftOf(dimension);
        if (current === null) return;
        const draft = { ...current, ...patch };
        if (!opsFor(draft.ruleType).includes(draft.operator)) draft.operator = opsFor(draft.ruleType)[0];
        setRuleDrafts(dimensionKey(dimension), { rule: draft });
    };

    // Texture-only skulls have no live item to sample from, so an operator uploads the PNG directly;
    // the server hosts it and hands back a real textures blob to store as identity.
    const uploadSkullTexture = async (dimension: SellbotDimensionDto, file: File) => {
        if (!file.type.includes('png') && !file.name.toLowerCase().endsWith('.png')) {
            toast?.error(t('SELLBOT_SKULL_UPLOAD_PNG_ONLY'));
            return;
        }
        if (file.size > 1_000_000) {
            toast?.error(t('SELLBOT_SKULL_UPLOAD_MAX_SIZE'));
            return;
        }
        const res = await api.sellbotSkullTexture.upload({ file });
        if (!isOk(res.data) || !res.data.value) {
            toast?.error(firstError(res.data) ?? t('SELLBOT_TOAST_UPDATE_FAILED'));
            return;
        }
        editRule(dimension, { stringValue: null, stringValue2: null, stringValue3: res.data.value.textureValue, operator: 'Eq' });
    };

    const releasedCount = createMemo(() => dimensions().filter(dimension => stateOf(dimension) === 'Ignored').length);

    // The preview answers "what would this offer match", so it reads the drafts and not what is stored.
    const draftedRules = createMemo(() =>
        dimensions().map(draftOf).filter((rule): rule is ItemMatchRuleRequest => rule !== null));

    // Rendered by the backend, the one place that knows how rules turn into an item. Debounced because
    // it re-renders on every keystroke in a rule field.
    const [previewItem, setPreviewItem] = createSignal<ItemStackDto | null>(null);
    const [previewPending, setPreviewPending] = createSignal(false);
    const previewRules = useDebounced(draftedRules, 250);
    let previewSequence = 0;

    createEffect(() => {
        const rules = previewRules();
        const sequence = ++previewSequence;
        setPreviewPending(true);
        void props.ruleApi.preview({ itemId: props.itemId, metadata: props.metadata, rules })
            .then(stack => {
                // A slower earlier render must not overwrite a newer one.
                if (sequence !== previewSequence) return;
                setPreviewPending(false);
                if (stack) setPreviewItem(stack);
            });
    });

    const previewStack = (): ItemStack => {
        const stack = previewItem();
        return stack
            ? { itemId: stack.itemId, count: 1, damage: stack.metadata, snbt: stack.snbt ?? undefined }
            : { itemId: props.itemId, count: 1, damage: props.metadata, snbt: props.snbt ?? undefined };
    };

    const dimensionLabel = (dimension: SellbotDimensionDto) => {
        return dimensionLabelOf(dimension, t);
    };

    // A row with no rule asserts the vanilla value, which on every dimension but the variant is absent.
    const dimensionSample = (dimension: SellbotDimensionDto) => {
        const state = stateOf(dimension);
        if (state === 'Ignored') return t('SELLBOT_DIM_ANY');
        if (state === 'Custom') return '';
        return t('SELLBOT_DIM_IS',
            originalValue(dimension, props.metadata) ?? t('SELLBOT_DIM_ABSENT'));
    };


    return (
                            <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                <div class={styles.sectionHeader}>
                                    <div class="h hcenter" style={{ 'justify-content': 'space-between' }}>
                                        <h2><Text val="SELLBOT_RULES" /></h2>
                                        {props.headerAction}
                                    </div>
                                    <span class={styles.helper}>{t(releasedCount() > 0 ? 'SELLBOT_RULES_HINT' : 'SELLBOT_RULES_EXACT_HINT')}</span>
                                </div>
                                <div class={styles.rulePreview} classList={{ [styles.rulePreviewStale]: previewPending() }}>
                                    <ItemStackView stack={previewStack()} inlineTooltip />
                                    <For each={forbiddenKinds()}>{(key) =>
                                        <span class={styles.ruleMeaning}><Text val={key} /></span>
                                    }</For>
                                    <span class={styles.helper}>{t(releasedCount() > 0 ? 'SELLBOT_RULES_PREVIEW_HINT' : 'SELLBOT_RULES_PREVIEW_EXACT_HINT')}</span>
                                </div>
                                <Show when={props.rules.length === 0}>
                                    <div class={styles.tierEmpty}><i class="fa-solid fa-cube" /> <Text val="SELLBOT_NO_RULES" /></div>
                                </Show>
                                <div class={styles.ruleList}>
                                    {(() => {
                                    let skullFileInput: HTMLInputElement | undefined;
                                    const dimensionCard = (dimension: SellbotDimensionDto, states: readonly string[]) => {
                                        const draft = () => draftOf(dimension)!;
                                        const dirty = () => isDimensionDirty(dimension);
                                        const stripColorsCheck = () => (
                                            <label class={styles.ruleCheck} title={t('SELLBOT_RULE_STRIP_COLORS')}>
                                                <input type="checkbox" checked={draft().stripColorCodes} onChange={(e) => editRule(dimension, { stripColorCodes: e.currentTarget.checked })} />
                                                <Text val="SELLBOT_RULE_STRIP_COLORS" />
                                            </label>
                                        );
                                        return (
                                        <div class={`${styles.ruleCard} ${dirty() ? styles.ruleCardDirty : ''}`}>
                                            <div class={styles.ruleCardHead}>
                                                <i class={`fa-solid ${ruleTypeIcon(dimension.kind)} ${styles.ruleTypeIcon}`} aria-hidden="true" />
                                                <span class={styles.ruleDesc}>{dimensionLabel(dimension)}</span>
                                                <span class={styles.ruleMeaning}>{dimensionSample(dimension)}</span>
                                                <span style={{ flex: 1 }} />
                                                <select class={styles.ruleOpSelect} value={stateOf(dimension)} onChange={(e) => setDimensionState(dimension, e.currentTarget.value)}>
                                                    <For each={states}>{(state) => <option value={state}>{t('SELLBOT_DIM_STATE_' + state)}</option>}</For>
                                                </select>
                                            </div>
                                            <Show when={stateOf(dimension) === 'Custom'}>
                                            <div class={styles.ruleCardHead}>
                                                <select class={styles.ruleOpSelect} value={draft().operator} onChange={(e) => editRule(dimension, { operator: e.currentTarget.value })}>
                                                    <For each={opsFor(dimension.kind)}>{(op) => <option value={op}>{t('SELLBOT_RULE_OP_' + op)}</option>}</For>
                                                </select>
                                                <Show when={comparesDamageField(dimension.kind) && itemVariants().length > 1}>
                                                    <select class={styles.ruleVariantSelect} value={String(draft().numericValue)}
                                                        onChange={(e) => editRule(dimension, { numericValue: Number(e.currentTarget.value) })}>
                                                        <Show when={!itemVariants().some(v => v.metadata === draft().numericValue)}>
                                                            <option value={String(draft().numericValue)}>{t('SELLBOT_RULE_VARIANT_UNKNOWN', draft().numericValue)}</option>
                                                        </Show>
                                                        <For each={itemVariants()}>{(variant) => <option value={String(variant.metadata)}>{variantLabel(variant)}</option>}</For>
                                                    </select>
                                                </Show>
                                                <Show when={(comparesDamageField(dimension.kind) && itemVariants().length <= 1) || dimension.kind === 'RepairCost' || (dimension.kind === 'Enchantment' && draft().operator !== 'Exists')}>
                                                    <input type="number" class={styles.ruleNum} value={draft().numericValue} placeholder={dimension.kind === 'Enchantment' ? t('SELLBOT_RULE_LEVEL') : t('SELLBOT_RULE_VALUE')}
                                                        onInput={(e) => editRule(dimension, { numericValue: Number(e.currentTarget.value || 0) })} />
                                                </Show>
                                            </div>
                                            <Show when={comparesDamageField(dimension.kind)}>
                                                <div class={styles.ruleCardBody}>
                                                    <ItemStackView stack={{ itemId: props.itemId, count: 1, damage: draft().numericValue, snbt: props.snbt ?? undefined }} />
                                                    <span class={styles.ruleMeaning}>
                                                        {dimension.kind === 'DurabilityRemaining' && itemDurabilityMax() !== undefined
                                                            ? t('SELLBOT_RULE_DAMAGE_MEANING', draft().numericValue, Math.max(0, itemDurabilityMax()! - draft().numericValue), itemDurabilityMax()!)
                                                            : t('SELLBOT_RULE_METADATA_MEANING', draft().numericValue)}
                                                    </span>
                                                </div>
                                            </Show>
                                            <Show when={dimension.kind === 'NbtTagCount'}>
                                                <div class={styles.ruleCardBody}>
                                                    <ItemStackView stack={{ itemId: props.itemId, count: 1, damage: props.metadata, snbt: props.snbt ?? undefined }} />
                                                    <span class={styles.ruleMeaning}>{t('SELLBOT_RULE_TAGCOUNT_MEANING', itemTagCount())}</span>
                                                </div>
                                            </Show>
                                            <Show when={dimension.kind === 'Enchantment'}>
                                                <div class={styles.ruleCardBody}>
                                                    <select class={styles.ruleEnchantSelect} value={draft().stringValue ?? ''}
                                                        onChange={(e) => editRule(dimension, { stringValue: e.currentTarget.value })}>
                                                        <For each={selectableEnchantmentIds(dimension)}>{(id) => <option value={String(id)}>{getEnchantmentName(id)}</option>}</For>
                                                    </select>
                                                </div>
                                            </Show>
                                            <Show when={dimension.kind === 'DisplayName'}>
                                                <div class={styles.ruleCardBody}>
                                                    <input type="text" class={styles.ruleText} value={draft().stringValue ?? ''} placeholder={t('SELLBOT_RULE_NAME_VALUE')} onInput={(e) => editRule(dimension, { stringValue: e.currentTarget.value })} />
                                                    {stripColorsCheck()}
                                                </div>
                                            </Show>
                                            <Show when={dimension.kind === 'Lore'}>
                                                <div class={styles.ruleCardBody}>
                                                    <label class={styles.ruleCheck} title={t('SELLBOT_RULE_LINE')}>
                                                        <Text val="SELLBOT_RULE_LINE" />
                                                        <input type="number" class={styles.ruleNum} min="1" value={draft().numericValue + 1}
                                                            onInput={(e) => editRule(dimension, { numericValue: Math.max(0, Number(e.currentTarget.value || 1) - 1) })} />
                                                    </label>
                                                    <input type="text" class={styles.ruleText} value={draft().stringValue ?? ''} placeholder={t('SELLBOT_RULE_LORE_VALUE')} onInput={(e) => editRule(dimension, { stringValue: e.currentTarget.value })} />
                                                    {stripColorsCheck()}
                                                </div>
                                            </Show>
                                            <Show when={dimension.kind === 'Signature'}>
                                                <div class={styles.ruleCardBody}>
                                                    <Show when={draft().operator !== 'Exists'}>
                                                        <input type="text" class={styles.ruleText} value={draft().stringValue ?? ''} placeholder={t('SELLBOT_RULE_SIG_SIGNER')} onInput={(e) => editRule(dimension, { stringValue: e.currentTarget.value })} />
                                                    </Show>
                                                    <input type="text" class={styles.ruleText} value={draft().stringValue3 ?? ''} placeholder={t('SELLBOT_RULE_SIG_DATE')} onInput={(e) => editRule(dimension, { stringValue3: e.currentTarget.value })} />
                                                    {stripColorsCheck()}
                                                    <div class={styles.ruleTextLines}>
                                                        <input type="text" value={signatureTextLines(draft().stringValue2 ?? '')[0] ?? ''} placeholder={t('SELLBOT_RULE_SIG_TEXT_1')}
                                                            onInput={(e) => editRule(dimension, { stringValue2: withSignatureTextLine(draft().stringValue2 ?? '', 0, e.currentTarget.value) })} />
                                                        <input type="text" value={signatureTextLines(draft().stringValue2 ?? '')[1] ?? ''} placeholder={t('SELLBOT_RULE_SIG_TEXT_2')}
                                                            onInput={(e) => editRule(dimension, { stringValue2: withSignatureTextLine(draft().stringValue2 ?? '', 1, e.currentTarget.value) })} />
                                                    </div>
                                                </div>
                                            </Show>
                                            <Show when={dimension.kind === 'NbtContains'}>
                                                <div class={styles.ruleCardBody}>
                                                    <input type="text" class={styles.ruleText} value={draft().stringValue ?? ''} placeholder={t('SELLBOT_RULE_PATH_VALUE')} onInput={(e) => editRule(dimension, { stringValue: e.currentTarget.value })} />
                                                    <Show when={draft().operator === 'Eq'}>
                                                        <input type="text" class={styles.ruleText} value={draft().stringValue2 ?? ''} placeholder={t('SELLBOT_RULE_NBT_EXPECTED')} onInput={(e) => editRule(dimension, { stringValue2: e.currentTarget.value })} />
                                                    </Show>
                                                </div>
                                            </Show>
                                            <Show when={dimension.kind === 'SkullOwner'}>
                                                <div class={styles.ruleCardBody}>
                                                    <ItemStackView stack={previewStack()} />
                                                    <div class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                                        <span class={styles.ruleMeaning}>
                                                            {draft().stringValue ?? draft().stringValue2
                                                                ?? (draft().stringValue3 ? t('SELLBOT_SKULL_TEXTURE_ONLY') : t('SELLBOT_SKULL_NO_IDENTITY'))}
                                                        </span>
                                                        <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                                                            <button type="button" class="btn btn-sm btn-secondary" onClick={async () => {
                                                                const picked = await showSkullOwnerPicker(draft().stringValue ?? undefined);
                                                                if (picked) editRule(dimension, { stringValue: picked.name, stringValue2: picked.uuid, stringValue3: null, operator: 'Eq' });
                                                            }}>
                                                                <i class="fa-solid fa-magnifying-glass" /> <Text val="SELLBOT_SKULL_PICK_PLAYER" />
                                                            </button>
                                                            <button type="button" class="btn btn-sm btn-secondary" onClick={() => skullFileInput?.click()}>
                                                                <i class="fa-solid fa-upload" /> <Text val="SELLBOT_SKULL_UPLOAD" />
                                                            </button>
                                                            <input type="file" accept="image/png" style={{ display: 'none' }}
                                                                ref={skullFileInput}
                                                                onChange={(e) => {
                                                                    const file = e.currentTarget.files?.[0];
                                                                    e.currentTarget.value = '';
                                                                    if (file) void uploadSkullTexture(dimension, file);
                                                                }} />
                                                            <button type="button" class="btn btn-sm btn-secondary"
                                                                disabled={!draft().stringValue && !draft().stringValue2 && !draft().stringValue3}
                                                                onClick={async () => {
                                                                    const skin = await resolveSkin({
                                                                        name: draft().stringValue ?? undefined,
                                                                        id: draft().stringValue2 ?? undefined,
                                                                        textureValue: draft().stringValue3 ?? undefined,
                                                                    });
                                                                    if (!skin) { toast?.error(t('SKULL_NO_OWNER')); return; }
                                                                    await downloadSkin(skin.skinUrl, skin.name ?? draft().stringValue ?? 'skin');
                                                                }}>
                                                                <i class="fa-solid fa-download" /> <Text val="SELLBOT_SKULL_DOWNLOAD" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Show>
                                            </Show>
                                            <Show when={stateOf(dimension) === 'Custom' || dirty()}>
                                                <div class={styles.ruleCardFoot}>
                                                    <Show when={stateOf(dimension) === 'Custom'}>
                                                        <span class={styles.ruleDesc}>{t('SELLBOT_RULE_DESC_' + dimension.kind)}</span>
                                                    </Show>
                                                    <Show when={dirty()}>
                                                        <span class={`${styles.badge} ${styles.badgeWarning}`}><Text val="SELLBOT_RULE_UNSAVED" /></span>
                                                        <button type="button" class="btn btn-sm btn-secondary" onClick={() => revertDimension(dimension)}><Text val="COMMON_CANCEL" /></button>
                                                        <button type="button" class="btn btn-sm btn-primary" onClick={() => void saveDimension(dimension)}><i class="fa-solid fa-check" /> <Text val="SELLBOT_SAVE" /></button>
                                                    </Show>
                                                </div>
                                            </Show>
                                        </div>
                                        );
                                    };

                                    const addEntry = (kind: string) => kind === 'Lore' ? void addLoreLine()
                                        : kind === 'Enchantment' ? void addEnchantment()
                                            : void addNbtTag();

                                    return (
                                    <For each={categories()}>{(kind) => (
                                        <Show when={isListKind(kind)}
                                            fallback={<Show when={categoryRow(kind)} keyed>
                                                {(categoryDimension) => dimensionCard(categoryDimension, DIMENSION_STATES)}
                                            </Show>}>
                                            <div class={styles.categoryCard}>
                                                <div class={styles.categoryHead}>
                                                    <i class={`fa-solid ${ruleTypeIcon(kind)} ${styles.ruleTypeIcon}`} aria-hidden="true" />
                                                    <span class={styles.categoryName}>{t('SELLBOT_CATEGORY_' + kind)}</span>
                                                    <Show when={categoryMode(kind) === 'Custom'}>
                                                        <span class={styles.ruleMeaning}>{t('SELLBOT_CATEGORY_COUNT', entryRows(kind).length)}</span>
                                                    </Show>
                                                    <span style={{ flex: 1 }} />
                                                    <select class={styles.ruleOpSelect} value={categoryMode(kind)}
                                                        disabled={categoryBusy() === kind}
                                                        onChange={(e) => void setCategoryMode(kind, e.currentTarget.value)}>
                                                        <For each={DIMENSION_STATES}>{(state) => <option value={state}>{t('SELLBOT_DIM_STATE_' + state)}</option>}</For>
                                                    </select>
                                                </div>
                                                <span class={styles.ruleDesc}>
                                                    {t('SELLBOT_CATEGORY_MODE_' + categoryMode(kind), t('SELLBOT_CATEGORY_' + kind))}
                                                </span>
                                                <Show when={categoryMode(kind) === 'Custom'}>
                                                    <div class={styles.categoryEntries}>
                                                        <For each={entryRows(kind)}>{(entry) => dimensionCard(entry, entryStates(stateOf(entry)))}</For>
                                                        <button type="button" class="btn btn-sm btn-secondary" onClick={() => addEntry(kind)}>
                                                            <i class="fa-solid fa-plus" /> {t('SELLBOT_CATEGORY_ADD', t('SELLBOT_CATEGORY_ENTRY_' + kind))}
                                                        </button>
                                                    </div>
                                                </Show>
                                            </div>
                                        </Show>
                                    )}</For>
                                    );
                                    })()}
                                </div>
                            </div>

    );
}
